/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import { useQuery, useLazyQuery, NetworkStatus } from '@apollo/client';
import PropTypes from 'prop-types';

import { Box } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import * as Q from '../../../../../../constants/graphqlQueries';

import './CustomRC.css';
import CustomTextField from '../../../../../components/Textfield/CustomTextField';
import CustomDropdown from '../../../../../components/Dropdown/CustomDropdown';
import CustomButtons from '../../../../../components/Button/CustomButtons';
import UserRoles from '../../../../../../constants/userRoles';

const theme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D5D5D5',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D5D5D5',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D5D5D5',
          },
        },
      },
    },
  },
});

const RuleCreationErrorMessages = {
  DuplicatedRuleName: 'A rule with the same name already exists. Please try again with a different name.'
}

const FieldIndex = {
  ruleName: 1, client: 2, program: 3, project: 4, site: 5
}

const $ALL = 'All';

const Custom = ({ handleCustomData, customPreview, handleBack }) => {
  const user = useSelector((state) => state.User);
  const [inputFieldDisabled, setInputFieldDisabled] = useState(0);

  const [ruleName, setRuleName] = useState('');
  const [ruleNameToEdit, setRuleNameToEdit] = useState(null);
  const [selectedClient, setSelectedClient] = useState();
  const [selectedProgram, setSelectedProgram] = useState();
  const [selectedProject, setSelectedProject] = useState();
  const [selectedSite, setSelectedSite] = useState();

  const [clients, setClients] = useState({});
  const [programs, setPrograms] = useState({});
  const [projects, setProjects] = useState({});
  const [sites, setSites] = useState({});

  const [userClickedBack, setUserClickedBack] = useState(true);

  const [disabledBtn, setDisabledBtn] = useState(true);
  const [clientParentAccessIdentifiers, setClientParentAccessIdentifiers] = useState(null);

  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // get the data
  const [getIsRuleNameDuplicated, { data: dataIsRuleDuplicatedByName, networkStatus }] = useLazyQuery(Q.VALIDATE_RULE_NAME, { notifyOnNetworkStatusChange: true });
  const { data: dataClients, refetch: refetchClients } = useQuery(Q.APP_ALERT_CLIENTS_GL_QUERY);
  const [getPrograms, { data: dataPrograms }] = useLazyQuery(Q.APP_ALERT_PROGRAMS_GL_QUERY);
  const [getProjects, { data: dataProjects }] = useLazyQuery(Q.APP_ALERT_PROJECTS_GL_QUERY);
  const { data: dataSites, refetch: refetchSites } = useQuery(Q.APP_ALERT_SITES_GL_QUERY);

  // Get user CLient Parent Access
  const { data: ClientParentAccess } = useQuery(Q.ADMIN_MANAGMENT_SEARCH_CLIENT_PARENT_ACCESS, { variables: { EmployeeId: `${user.EmployeeId}` } });

  // Set Dropdown status
  const onChangeClient = (client) => {
    setSelectedClient(client);
    if (selectedProgram) setSelectedProgram(null);
    if (selectedProject) setSelectedProject(null);
    if (selectedSite) setSelectedSite(null);
    if (client) getPrograms({ variables: { ClientId: client.ClientId } });
  }
  const onChangeProgram = (program) => {
    setSelectedProgram(program);
    if (selectedProject) setSelectedProject(null);
    if (selectedSite) setSelectedSite(null);
    if (program) getProjects({ variables: { ProgramId: program.ProgramId } });
  }
  const onChangeProject = (project) => {
    setSelectedProject(project);
    if (selectedSite) setSelectedSite(null);
  }
  const onChangeSite = (site) => setSelectedSite(site);
  // Set text field status
  const handleRuleName = (value) => setRuleName(value);
  const handleBackPage = (status) => {
    if (status === 'Cancel') {
      setRuleName('');
      setSelectedClient(null);
      setSelectedProgram(null);
      setSelectedProject(null);
      setSelectedSite(null);
      handleBack(status);
    }
  }
  const submitEditCreate = () => {
    const customDataToCreate = [{
      ruleName,
      clientsID: selectedClient.ClientId,
      clients: selectedClient.DescriptionShort,
      programsID: selectedProgram.ProgramId,
      programs: selectedProgram.DescriptionShort,
      projectsID: selectedProject.ProjectId,
      projects: selectedProject.DescriptionShort,
      sitesID: selectedSite.SiteId,
      sites: selectedSite.DescriptionShort
    }];
    handleCustomData(customDataToCreate);
  }
  const validateRuleName = () => {
    setLoadingSubmit(true);
    getIsRuleNameDuplicated({
      variables: {
        RuleName: ruleName
      }
    });
  }
  const isInputFieldDisabled = (inputIndex) => {
    if (inputFieldDisabled && inputIndex > inputFieldDisabled) {
      return true;
    }

    return false;
  }

  useEffect(() => {
    if (ClientParentAccess) {
      if (ClientParentAccess.getWFMClientParentAccess) {
        const clientsId = ClientParentAccess.getWFMClientParentAccess.map(clientAccess => clientAccess.ClientCode);

        setClientParentAccessIdentifiers({
          clients: clientsId
        });
      }
    }
  }, [ClientParentAccess]);

  useEffect(() => {
    if (dataProjects) {
      let projectsData = [];

      projectsData = dataProjects.getProjects;

      const projectProps = {
        options: projectsData,
        getOptionLabel: (option) => option.ProjectId === $ALL
          ? option.DescriptionShort
          : `${option.DescriptionShort} (${option.ProjectId})`
      }
      setProjects(projectProps);
    }
  }, [dataProjects]);

  useEffect(() => {
    if (dataPrograms) {
      let programsData = [];

      programsData = dataPrograms.getPrograms;

      const programProps = {
        options: programsData,
        getOptionLabel: (option) => option.ProgramId === $ALL
          ? option.DescriptionShort
          : `${option.DescriptionShort} (${option.ProgramId})`
      }
      setPrograms(programProps);
    }
  }, [dataPrograms]);

  useEffect(() => {
    if (dataClients && clientParentAccessIdentifiers) {
      let clientsData = [];

      if (user.Role === UserRoles.Admin || user.Role === UserRoles.Owner) {
        clientsData = dataClients.getClients;
      }
      else {
        clientsData = dataClients.getClients.filter(client => clientParentAccessIdentifiers.clients.includes(client.ClientId));
      }

      const clientProps = {
        options: clientsData,
        getOptionLabel: (option) => option.ClientId === $ALL
          ? option.DescriptionShort
          : `${option.DescriptionShort} (${option.ClientId})`
      }
      setClients(clientProps);
    }
  }, [dataClients, clientParentAccessIdentifiers]);

  useEffect(() => {
    if (dataSites) {
      if (dataSites.getSites) {
        const sitesOptions = [{ SiteId: $ALL, DescriptionShort: $ALL }, ...dataSites.getSites];
        const siteProps = {
          options: sitesOptions,
          getOptionLabel: (option) => option.SiteId === $ALL
            ? option.DescriptionShort
            : `${option.DescriptionShort} (${option.SiteId})`
        }

        setSites(siteProps);
      }
    }
  }, [dataSites]);

  useEffect(() => {
    if (customPreview.length) {
      if (!Object.keys(clients).length && !Object.keys(programs).length && !Object.keys(projects).length && !Object.keys(sites).length) {
        const fetchData = async () => {
          getPrograms({ variables: { ClientId: customPreview[0].clientsID } });
          getProjects({ variables: { ProgramId: customPreview[0].programsID } });
          await refetchSites();
          await refetchClients();
          setUserClickedBack(true);
        }
        fetchData();
      }
    }
  }, [customPreview]);

  useEffect(() => {
    if (customPreview.length && userClickedBack && Object.keys(clients).length && Object.keys(programs).length && Object.keys(projects).length && Object.keys(sites).length) {
      setRuleName(customPreview[0].ruleName);
      setRuleNameToEdit(customPreview[0].ruleName);
      setSelectedClient({ DescriptionShort: customPreview[0].clients, ClientId: customPreview[0].clientsID });
      setSelectedProgram({ DescriptionShort: customPreview[0].programs, ProgramId: customPreview[0].programsID });
      setSelectedProject({ DescriptionShort: customPreview[0].projects, ProjectId: customPreview[0].projectsID });
      setSelectedSite({ DescriptionShort: customPreview[0].sites, SiteId: customPreview[0].sitesID });
      setDisabledBtn(false);
      setUserClickedBack(false);
    }
  }, [userClickedBack, clients, programs, projects, sites]);

  useEffect(() => {
    setUserClickedBack(false);
  }, []);

  useEffect(() => {
    if (dataIsRuleDuplicatedByName && networkStatus === NetworkStatus.ready) {
      if (!(dataIsRuleDuplicatedByName.isRuleDuplicatedByName) || ruleName === ruleNameToEdit) submitEditCreate();
      else toast(RuleCreationErrorMessages.DuplicatedRuleName);
      setLoadingSubmit(false);
    }
  }, [dataIsRuleDuplicatedByName, networkStatus]);

  useEffect(() => {
    // State Watcher
    if (!ruleName) setInputFieldDisabled(FieldIndex.ruleName);
    else if (!selectedClient) setInputFieldDisabled(FieldIndex.client);
    else if (!selectedProgram) setInputFieldDisabled(FieldIndex.program);
    else if (!selectedProject) setInputFieldDisabled(FieldIndex.project);
    else if (!selectedSite) setInputFieldDisabled(FieldIndex.site); 
    else {
      setInputFieldDisabled(null);
    }
  }, [ruleName, selectedClient, selectedProgram, selectedProject, selectedSite]);

  useEffect(() => {
    if (inputFieldDisabled === null) setDisabledBtn(false);
    else setDisabledBtn(true);
  }, [inputFieldDisabled]);

  return (
    <ThemeProvider theme={theme}>
      <Box className='custom__row'>
        <CustomTextField
          title='Rule Name'
          value={ruleName}
          handleChange={handleRuleName}
          placeholder='Input text'
          mandatory
          loading={userClickedBack}
          maxLength={25}
        />
      </Box>
      <Box className='customn__wrapper'>
        <Box className='custom__wrapper__container'>
          <Box className='custom__wrapper__box'>
            <CustomDropdown
              title='Client'
              value={selectedClient}
              handleChange={onChangeClient}
              defaultProps={clients}
              disabled={isInputFieldDisabled(FieldIndex.client)}
              mandatory
              loading={userClickedBack}
            />
            <CustomDropdown
              title='Program'
              value={selectedProgram}
              handleChange={onChangeProgram}
              defaultProps={programs}
              disabled={isInputFieldDisabled(FieldIndex.program)}
              mandatory
              loading={userClickedBack}
            />
            <CustomDropdown
              title='Project'
              value={selectedProject}
              handleChange={onChangeProject}
              defaultProps={projects}
              disabled={isInputFieldDisabled(FieldIndex.project)}
              mandatory
              loading={userClickedBack}
            />
            <CustomDropdown
              title='Site'
              value={selectedSite}
              handleChange={onChangeSite}
              defaultProps={sites}
              disabled={isInputFieldDisabled(FieldIndex.site)}
              mandatory
              loading={userClickedBack}
            />
          </Box>
        </Box>
      </Box>
      <div className='create__wrapper_btn'>
        <CustomButtons
          handleClick={() => handleBackPage('Cancel')}
          name='Cancel'
          loading={userClickedBack}
        />
        <CustomButtons
          handleClick={validateRuleName}
          name='Next'
          disabled={disabledBtn}
          tooltipFree
          loadingButton={loadingSubmit}
          loading={userClickedBack}
        />
      </div>
    </ThemeProvider>
  );
}

Custom.propTypes = {
  handleCustomData: PropTypes.func.isRequired,
  customPreview: PropTypes.array,
  handleBack: PropTypes.func
}

Custom.defaultProps = {
  customPreview: [],
  handleBack: null
}

export default Custom;
